/**
 * Quote Search Service
 *
 * Four search modes exposed to the /quotes page (QuotesList + QuoteSearchBar):
 *   - by date         → matches next_flight_date on the quote's first-leg itinerary
 *   - by airport      → matches departure_airport OR arrival_airport
 *   - by client       → matches client_id (exact)
 *   - by quote number → partial (substring) match on quote_number OR invoice_number
 *
 * All four now hit the unified GET /financial/reports endpoint (the same one
 * that powers /financials/reports). The endpoint returns a paged UNION of
 * quotes + invoices — so a client search naturally surfaces both quote rows
 * and the invoices attached to that client, matching the merged view the
 * /quotes page was already building client-side.
 *
 * Signature preserved from the previous version:
 *     (value, extraFilters, skip, limit) → { data: Row[], hasMore: boolean }
 *
 * `extraFilters` is passed through as additional query params (e.g.
 * { status_search: 'DRAFT' } to respect the current tab). Role-based
 * scope (self / org / platform) is derived server-side from the auth
 * token — callers no longer need to pass broker_org_id or created_by.
 */
import { apiClient } from '@/lib/api';

// Match the include chain used by /financials — every field QuoteCard reads.
const QUOTES_INCLUDE =
  'client,creator,sender,payments,items,bid_items,bid_items.bid_options,' +
  'bid_items.bid_options.aircraft,bid_items.bid_options.aircraft.custom_profile,' +
  'bid_items.bid_options.operator';

async function fetchReports(params, skip, limit) {
  const { items, has_more } = await apiClient.getFinancialReports({
    include: QUOTES_INCLUDE,
    sort_by: 'created_at',
    sort_order: 'desc',
    ...params,
    skip,
    limit,
  });
  const data = Array.isArray(items) ? items : [];
  // Fall back on "short page" heuristic if the BE didn't set has_more —
  // matches the getNextPageParam contract QuotesList relies on.
  const hasMore = typeof has_more === 'boolean' ? has_more : data.length === limit;
  return { data, hasMore };
}

/**
 * Search quotes/invoices whose departure_date (the quote's top-level date
 * column) falls on the given YYYY-MM-DD day. Uses the BE's
 * departure_date_from/to range — matches the previous getQuotes semantic
 * exactly. Invoice rows join on the parent quote's departure_date, so
 * parentless ADVANCE invoices are dropped when this filter is set.
 *
 * NOTE: the previous implementation ALSO matched return_date via a second
 * parallel call. /financial/reports doesn't expose return_date filters,
 * so return-leg-only matches are dropped. Ask BE to add
 * return_date_from/to if product wants them back.
 */
export async function searchQuotesByDate(date, extraFilters = {}, skip = 0, limit = 10) {
  if (!date) return { data: [], hasMore: false };
  return fetchReports({
    ...extraFilters,
    departure_date_from: date,
    departure_date_to: date,
  }, skip, limit);
}

/**
 * Search quotes/invoices whose departure_airport OR arrival_airport matches
 * (substring, min 2 chars). BE `airport_search` covers both endpoints in a
 * single query, so no more dual-call OR-via-dedup pattern.
 */
export async function searchQuotesByAirport(airportCode, extraFilters = {}, skip = 0, limit = 10) {
  if (!airportCode) return { data: [], hasMore: false };
  const code = String(airportCode).trim();
  if (code.length < 2) return { data: [], hasMore: false };
  return fetchReports({
    ...extraFilters,
    airport_search: code,
  }, skip, limit);
}

/**
 * Search quotes/invoices for a specific client_id (exact UUID match).
 */
export async function searchQuotesByClient(clientId, extraFilters = {}, skip = 0, limit = 10) {
  if (!clientId) return { data: [], hasMore: false };
  return fetchReports({
    ...extraFilters,
    client_id: clientId,
  }, skip, limit);
}

/**
 * Partial (substring) match on quote_number OR invoice_number. BE
 * `reference_no_search` requires min 2 chars — QuoteSearchBar enforces a
 * stricter min length (4 chars, per QUOTE_NUMBER_MIN_LENGTH) upstream.
 */
export async function searchQuotesByQuoteNumber(query, extraFilters = {}, skip = 0, limit = 10) {
  const q = String(query || '').trim();
  if (q.length < 2) return { data: [], hasMore: false };
  return fetchReports({
    ...extraFilters,
    reference_no_search: q,
  }, skip, limit);
}
